/**
 * @file FontRoleCard.tsx
 * @description Карточка шрифта роли (phase19.16 Р1, Р1-7; uiux-design-r1 §1
 * «Вкладка Шрифты», SB99; uiux-fonts-usage §3, §6): название шрифта, у
 * своего — пилюля «ваш» (и «без русских букв ⚠»), «Сменить», у акцента —
 * «Убрать», образец роли. Шрифт, снятый из каталога, — с пояснением (у
 * магазина он остаётся, пока владелец его не сменит). Название — из реестра
 * шрифтов магазина.
 *
 * Невыбранная необязательная роль (акцент) — «Не выбран [Выбрать]» и ниже
 * «Сейчас вместо него — шрифт заголовков (Montserrat):» с образцом
 * запасного шрифта (`effectiveFontKey`), чтобы было видно, что стоит на сайте.
 * Название роли и назначение рисует `FontRoleSection`.
 */

import { FONT_ROLES, isShopFontEntry, isShopFontKey, type FontRole } from "@chaiberi/shared";

import { Button } from "../../ui/button";
import { useShopFonts } from "../shop-fonts-context";
import { FontSampleText } from "./FontSampleText";
import { NoCyrillicBadge, OwnFontBadge } from "./ShopFontRow";

/** Запасной шрифт невыбранной роли: чей и какой ключ стоит на сайте. */
export interface FontRoleFallback {
  role: FontRole;
  fontKey: string;
}

interface Props {
  fontKey: string | null;
  sample: string;
  sampleSize: number;
  weight?: number;
  onChange: () => void;
  onRemove?: () => void;
  /** Что стоит на сайте, пока роль не выбрана (акцент → шрифт заголовков). */
  fallback?: FontRoleFallback;
}

export function FontRoleCard({ fontKey, sample, sampleSize, weight = 400, onChange, onRemove, fallback }: Props) {
  const { registry } = useShopFonts();
  const entry = fontKey ? registry.find(fontKey) : undefined;
  const own = entry !== undefined && isShopFontEntry(entry) ? entry : null;
  // Свой шрифт, которого нет в списке (нет права на список или его удалили).
  const unknownOwn = !entry && fontKey !== null && isShopFontKey(fontKey);
  const family = entry?.family ?? (unknownOwn ? "Свой шрифт" : "Не выбран");
  const retired = entry !== undefined && !isShopFontEntry(entry) && entry.retired === true;

  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="type-body min-w-0 break-words font-medium text-on-surface">{family}</span>
          {own || unknownOwn ? <OwnFontBadge /> : null}
          {own && !own.hasCyrillic ? <NoCyrillicBadge /> : null}
        </span>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="min-h-12 lg:min-h-10" onClick={onChange}>
            {fontKey ? "Сменить" : "Выбрать"}
          </Button>
          {onRemove && fontKey ? (
            <Button variant="ghost" className="min-h-12 lg:min-h-10" onClick={onRemove}>
              Убрать
            </Button>
          ) : null}
        </div>
      </div>
      {fontKey ? <FontSampleText fontKey={fontKey} text={sample} size={sampleSize} weight={weight} lines={2} /> : null}
      {!fontKey && fallback ? <FallbackSample fallback={fallback} sample={sample} size={sampleSize} /> : null}
      {retired && entry ? (
        <p className="type-caption text-on-surface-variant">
          Шрифт «{entry.family}» больше не предлагается. На сайте он остаётся, пока вы его не смените.
        </p>
      ) : null}
    </div>
  );
}

/** «Сейчас вместо него — шрифт заголовков (Montserrat):» + образец запасным шрифтом. */
function FallbackSample({ fallback, sample, size }: { fallback: FontRoleFallback; sample: string; size: number }) {
  const { registry } = useShopFonts();
  const name = registry.find(fallback.fontKey)?.family ?? (isShopFontKey(fallback.fontKey) ? "свой шрифт" : fallback.fontKey);
  return (
    <>
      <p className="type-caption text-on-surface-variant">
        Сейчас вместо него — шрифт {FONT_ROLES[fallback.role].genitive} ({name}):
      </p>
      <FontSampleText fontKey={fallback.fontKey} text={sample} size={size} lines={2} />
    </>
  );
}
